import React from 'react';
import styled, { css } from 'styled-components/macro';
import capitalize from 'lodash.capitalize';
import pluralize from 'helpers/pluralize';

import { getEntityTypes } from 'maps/entity-types';
import { SalesTypeValues } from 'maps/sales-types';
import { ILabelValuePair } from 'types/shared';
import { IMetric } from 'maps/metrics';

import handleBrowseEntityType from './../shared/handleBrowseEntityType';

import Box from 'components/elements/Box';
import Controls from 'components/base/TableControls/TableControls';
import Dropdown from 'components/base/Dropdown/Dropdown';
import { MenuList } from 'components/base/Menu/Menu';
import MobileSortingControls from 'components/data-visualization/Table/Sort';
import RadioGroup, { Radio } from 'components/base/RadioGroup/RadioGroup';
import SearchField from 'components/styled/SearchField/SearchField';
import { ButtonWrapper } from 'components/base/Dropdown/shared/styles';
import { Item } from 'components/base/Dropdown/shared/styles';
import { SearchFieldBlock } from '../shared/styles';
import SalesDropdown from './SalesTypeDropdown';
import { MenuRoot } from 'components/base/Menu/index';
import SalesTypeDropdown from 'components/utility/SalesTypeDropdown';
import Switcher from 'components/Switcher';
import useAllSalesOptions from './hooks/useAllSalesOptions';
import theme from 'styles/themes/variables';

export const RadioGroupTabsWrapper = styled('div')`
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};

  ${Radio} {
    & + label {
      flex: 0 1 auto;
      padding: 1rem 0.875rem;
      background: transparent !important;
      text-transform: uppercase;
      color: ${({ theme }): string => theme.CasperGrey};
      line-height: 1;
      font-size: 1rem;
    }
    &:checked + label {
      position: relative;
      color: ${({ theme }): string => theme.NightBlue};

      &:after {
        position: absolute;
        top: 100%;
        display: block;
        width: 100%;
        height: 4px;
        background-color: ${({ theme }): string => theme.AccentGreen};
        content: '';
      }
    }
    &:hover:not(:checked) + label {
      background-color: ${({ theme }): string => theme.Sand} !important;
    }
  }
`;

export const TableControls = styled(Controls)<any>`
  display: block;
  padding: ${({ theme }): string => theme.spacing.small};
  @media screen and (min-width: 40em) {
    padding: ${({ theme, browseRelated }): string =>
      browseRelated
        ? '0;'
        : `${theme.spacing.small} ${theme.spacing.xlarge} ${theme.spacing.large};`};
  }
  ${MenuList} {
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  }
  ${ButtonWrapper} {
    padding: ${({ theme, isTopList }): string =>
      `${isTopList ? theme.spacing.medium : theme.spacing.large} ${
        theme.spacing.large
      }`};
    color: #373737;
  }
  ${Item} {
    padding: 2px ${({ theme }): string => theme.spacing.medium};
    font-size: 13px;
    font-weight: 600;
    line-height: 2.08;
    letter-spacing: 1.18px;
    color: ${({ theme }): string => theme.ShadowBlack};
  }
  ${MenuRoot} {
    margin-left: 0;
  }
`;

const SalesTypeWrapper = styled('div')<any>`
  ${({ isTopLists }): any =>
    isTopLists
      ? css`
          margin: 5px 0 0;

          button {
            ${ButtonWrapper} {
              padding: ${({ theme }): string =>
                `${theme.spacing.medium} ${theme.spacing.large} !important`};
            }
          }
        `
      : '-5px 0 0'};
`;

const defaultActiveItemForMobile = {
  change: 'performance_wow_in_progress',
  key: 'performance_this_week_to_date',
  label: 'Week to Date (High to Low)',
  range: 'performance_this_week_to_date_range',
  sortOrder: 'desc',
  value: 'performance_this_week_to_date',
  withoutSubLabel: true,
};

interface IBrowseControlsProps {
  handleSearch: any;
  isSearchHidden?: boolean;
  value: string;
  browseEntityType: string;
  isLargeScreen: boolean;
  salesType: ILabelValuePair<SalesTypeValues> & {
    base?: string | undefined;
    split?: string | undefined;
  };
  saveComponentSettings: (settings: {
    selectedSalesType: string;
    browseEntityType: string;
  }) => void;
  isExternalUser: boolean;
  isExternalOneLabelUser: boolean;
  sortOrder: string;
  handleToggle: Function;
  handleDropdown: Function;
  dropdownItems: (IMetric | { label: string; value: string })[];
  activeItem: IMetric;
  tableName?: string;
  browseRelated?: boolean;
  globalFilters?: any;
  isTabControlsHidden?: boolean;
  isSalesTypeMobile?: boolean;
  isReleaseChecked: boolean;
  setReleaseChecked: Function;
  isRoundedDropdown?: string;
}

const BrowseControls: React.FC<IBrowseControlsProps> = (props) => {
  const {
    handleSearch,
    isSearchHidden,
    value,
    browseEntityType,
    isLargeScreen,
    salesType,
    saveComponentSettings,
    isExternalUser,
    isExternalOneLabelUser,
    sortOrder,
    handleToggle,
    handleDropdown,
    dropdownItems,
    activeItem,
    tableName,
    globalFilters,
    browseRelated,
    isTabControlsHidden = false,
    isSalesTypeMobile = false,
    isReleaseChecked,
    setReleaseChecked,
    isRoundedDropdown,
  } = props;
  const hasMedia = !!globalFilters?.media;

  const allSalesOptions = useAllSalesOptions(
    isExternalUser,
    tableName,
    browseEntityType,
    hasMedia
  );

  const itemToString = (item: any): string | false => {
    if (item) {
      return `${item.value}-${item.sortOrder}`;
    }
    return false;
  };

  const activeItemMobile =
    activeItem.label === 'This week to date'
      ? defaultActiveItemForMobile
      : activeItem;

  const entitiesPlaceholder =
    tableName !== 'customLists'
      ? ''
      : browseEntityType && pluralize(browseEntityType);
  const isReleaseOnBrowseOpus =
    browseEntityType === 'release' && tableName === 'BrowseOpus';

  return (
    <TableControls
      browseRelated={browseRelated}
      isTopList={tableName === 'TopLists'}
    >
      {!isSearchHidden && (
        <SearchFieldBlock>
          <SearchField
            handleSearch={handleSearch}
            value={value}
            placeholder={`Search ${entitiesPlaceholder}`}
          />
        </SearchFieldBlock>
      )}
      {tableName !== 'customLists' &&
        tableName !== 'TopLists' &&
        (isLargeScreen ? (
          <RadioGroupTabsWrapper>
            {!isTabControlsHidden && (
              <RadioGroup
                name="browse-entity-type"
                options={getEntityTypes(isExternalOneLabelUser, tableName)}
                selectedOption={browseEntityType}
                onChange={(item: { label: string; value: any }): void =>
                  handleBrowseEntityType({
                    value: item.value,
                    isExternalUser,
                    salesType,
                    saveComponentSettings,
                  })
                }
              />
            )}
          </RadioGroupTabsWrapper>
        ) : (
          <Dropdown
            items={getEntityTypes(isExternalUser, tableName)}
            selectedItem={{
              value: browseEntityType,
              label: capitalize(pluralize(browseEntityType)),
            }}
            onChange={(item: { label: string; value: any }): void =>
              handleBrowseEntityType({
                value: item.value,
                isExternalUser,
                salesType,
                saveComponentSettings,
              })
            }
            isToggleCaret={true}
            isRoundedDropdown="all"
          />
        ))}
      {!isLargeScreen && (
        <MobileSortingControls
          hide={isLargeScreen}
          sortOrder={sortOrder}
          handleToggle={handleToggle}
          handleDropdown={handleDropdown}
          dropdownItems={dropdownItems}
          activeItem={activeItemMobile}
          itemToString={itemToString}
          isToggleCaret={true}
          isToggleShown={false}
          buttonTextColor={theme.colors.White}
          isRoundedDropdown={
            isSalesTypeMobile && !isLargeScreen ? 'all' : isRoundedDropdown
          }
        />
      )}
      {browseEntityType !== 'label' &&
        (isLargeScreen || isSalesTypeMobile ? (
          <>
            <Box
              display="flex"
              justifyContent={
                isReleaseOnBrowseOpus ? 'space-between' : 'flex-end'
              }
            >
              {isReleaseOnBrowseOpus && (
                <Box alignSelf={isLargeScreen ? 'center' : 'flex-end'}>
                  <Switcher
                    componentName={tableName}
                    isChecked={isReleaseChecked}
                    leftLabel="View Releases"
                    rightLabel="View Compilations"
                    setCheck={setReleaseChecked}
                  />
                </Box>
              )}
              <SalesTypeWrapper isTopLists={isSalesTypeMobile}>
                <SalesDropdown
                  salesType={salesType}
                  allSalesOptions={allSalesOptions}
                  saveComponentSettings={saveComponentSettings}
                  browseEntityType={browseEntityType}
                  isExternalUser={isExternalUser}
                  isMobile={isTabControlsHidden}
                  isRoundedDropdown={isRoundedDropdown}
                />
              </SalesTypeWrapper>
            </Box>
          </>
        ) : (
          <SalesTypeDropdown
            entityType={browseEntityType}
            selectedItem={salesType}
            onChange={(option: { label: string; value: string }): void =>
              saveComponentSettings({
                selectedSalesType: option.value,
                browseEntityType,
              })
            }
            isToggleCaret={true}
            tableName={tableName}
            hasMedia={!!globalFilters?.media}
            isRoundedDropdown={isRoundedDropdown}
          />
        ))}

      {!isLargeScreen && !isSalesTypeMobile && isReleaseOnBrowseOpus && (
        <Box>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            my={3}
          >
            <Switcher
              componentName={tableName}
              isChecked={isReleaseChecked}
              leftLabel="View Releases"
              rightLabel="View Compilations"
              setCheck={setReleaseChecked}
            />
          </Box>
        </Box>
      )}
    </TableControls>
  );
};

export default BrowseControls;
